/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useUser } from './account';
import { useSetting } from './settings';
import {
  DEFAULT_ALLSTAR_DOMAINS,
  isAllStarEmail,
} from '../utils/allStarDomain';

/**
 * Whether the logged-in user is an AirAsia "Allstar" (internal staff), judged by
 * matching their account email domain against a configurable allow-list. The
 * list defaults to {@link DEFAULT_ALLSTAR_DOMAINS} and can be overridden via the
 * `allstar_domains` setting (comma-separated, with or without a leading `@`).
 *
 * @param caller Identifier of the caller, forwarded to `useUser` for error logs.
 */
export const useIsAllStar = (
  caller: string = 'useIsAllStar',
): boolean | undefined => {
  const { data: user } = useUser(caller);
  const { value: domainsSetting } = useSetting('allstar_domains');

  return useMemo(() => {
    if (!user?.username) return undefined;
    return isAllStarEmail(user.username, domainsSetting);
  }, [user?.username, domainsSetting]);
};
